import { useMemo } from 'react';

import { useCustomer } from '@order/Customer';
import type { TranslationKeys } from '@order/Localization';

import { isAddressDuplicateByName } from '../../utils';

/**
 * Returns a memoed result of address duplication by name.
 */
export function useAddressDuplicateByName(props: AddressDuplicateByNameProps) {
  const { addressId, addressName, isCustomNameTouched } = props;

  const { customer } = useCustomer();
  const addresses = customer?.addresses;

  return useMemo(() => {
    const hasHomeAddress = isAddressDuplicateByName({
      addressId,
      addressName: 'home',
      addresses: addresses ?? [],
    });

    const hasWorkAddress = isAddressDuplicateByName({
      addressId,
      addressName: 'work',
      addresses: addresses ?? [],
    });

    const isCustomNameMissing = isCustomNameTouched && !addressName;
    const isCustomNameDuplicate =
      isCustomNameTouched &&
      isAddressDuplicateByName({
        addressId,
        addressName,
        addresses: addresses ?? [],
      });

    const isCustomNameInvalid = isCustomNameDuplicate || isCustomNameMissing;

    const customNameNotice = getCustomNameNotice({
      isCustomNameDuplicate,
      isCustomNameMissing,
    });

    return {
      hasHomeAddress,
      hasWorkAddress,
      isCustomNameInvalid,
      customNameNotice,
    };
  }, [addressId, addressName, addresses, isCustomNameTouched]);
}

// ─── Helpers ───────────────────────────────────────────────────────────

function getCustomNameNotice(
  props: CustomNameNoticeProps,
): TranslationKeys | undefined {
  if (props.isCustomNameDuplicate) {
    return 'addresses.error.name.duplicate';
  }

  if (props.isCustomNameMissing) {
    return 'addresses.required';
  }
}

// ─── Types ─────────────────────────────────────────────────────────────

type AddressDuplicateByNameProps = Readonly<{
  addressId: string | undefined;
  addressName?: string;
  isCustomNameTouched: boolean;
}>;

type CustomNameNoticeProps = Readonly<{
  isCustomNameDuplicate?: boolean;
  isCustomNameMissing?: boolean;
}>;
